import { TrendingUp } from "lucide-react";
import formatCurrency from "@/utils/formatCurrency";
import CurrencyInput from "@/components/shared/CurrencyInput";
import { useInflationCalculator } from "@/hooks/useCalculators";

const InflationCalculatorTab = () => {
  const { currentExpense, setCurrentExpense, inflationRate, setInflationRate, years, setYears, result } = useInflationCalculator();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="finance-card">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2"><TrendingUp className="text-teal-600" size={24} /> Inflation Calculator</h2>
        <p className="text-gray-600 mb-6">Calculate the impact of inflation on your money. Find out how much you will need in the future to meet your current expenses whilst keeping up with inflation.</p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Value of Current Expenses</label>
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center border-2 border-gray-200 rounded-lg px-4 py-3 focus-within:border-blue-500">
              <CurrencyInput value={currentExpense} onChange={(v) => setCurrentExpense(v)} className="flex-1 outline-none text-lg font-semibold text-gray-800" />
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm text-gray-500">Future Cost:</p>
              <p className="text-xl font-bold text-teal-600">₹{formatCurrency(result.futureCost)}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-700">Annual Inflation Rate (%)</label>
            <div className="bg-gray-100 px-4 py-2 rounded-lg flex items-center gap-1">
              <input type="number" value={inflationRate} onChange={(e) => setInflationRate(Number(e.target.value))} className="w-12 bg-transparent text-lg font-bold text-gray-800 outline-none text-center" step="0.5" />
              <span className="text-gray-500">%</span>
            </div>
          </div>
          <input type="range" min="1" max="15" step="0.5" value={inflationRate} onChange={(e) => setInflationRate(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          <div className="flex justify-between text-xs text-gray-500 mt-1"><span>1%</span><span>15%</span></div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-700">Time Period (In Years)</label>
            <div className="bg-gray-100 px-4 py-2 rounded-lg flex items-center gap-1">
              <input type="number" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-12 bg-transparent text-lg font-bold text-gray-800 outline-none text-center" />
              <span className="text-gray-500">Y</span>
            </div>
          </div>
          <input type="range" min="1" max="50" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
          <div className="flex justify-between text-xs text-gray-500 mt-1"><span>1 Year</span><span>50 Years</span></div>
        </div>
      </div>

      <div className="finance-card">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inflation Impact Summary</h3>
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">Today's Value</p>
            <p className="text-2xl font-bold text-blue-600">₹{formatCurrency(currentExpense)}</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-700">After {years} Years at {inflationRate}% Inflation</p>
            <p className="text-3xl font-bold text-orange-600">₹{formatCurrency(result.futureCost)}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700">Increase Due to Inflation</p>
            <p className="text-2xl font-bold text-red-600">₹{formatCurrency(result.futureCost - currentExpense)}</p>
            <p className="text-sm text-red-500 mt-1">{((result.futureCost / currentExpense - 1) * 100).toFixed(0)}% increase over {years} years</p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">What this means:</span> An expense of <span className="font-bold text-blue-600">₹{formatCurrency(currentExpense)}</span> today will cost <span className="font-bold text-orange-600">₹{formatCurrency(result.futureCost)}</span> after {years} years at {inflationRate}% annual inflation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InflationCalculatorTab;
